import api from '@/services/api';
import type {
    SmriqContext,
    SmriqVehicleConfirmResult,
    SmriqSchedulePreviewResult,
    SmriqGenerateResult,
    SmriqOperationExclusion,
} from '../types/smriq.types';

const smrPath = '/smr';

export interface ConfirmSmriqVehiclePayload {
    vrm: string;
    current_mileage: number;
    annual_mileage: number;
}

export type SmriqLastCompletedServiceInput =
    | { mode: 'none' }
    | {
          mode: 'selected';
          service_option_id: string;
          completed_date: string;
          completed_mileage: number;
      }
    | { mode: 'auto'; completed_date: string; completed_mileage: number };

export interface GenerateSmriqSchedulePayload {
    service_plan_session_id: string;
    plan_start_date: string;
    annual_mileage: number;
    duration_months: number;
    include_mot_tests: boolean;
    /** Capped at 50 server-side (max:50); the UI must not send more. Each entry is a
     *  {service_id, operation_id} pair (visit id + recommended work-item id). */
    recommended_operation_exclusions?: SmriqOperationExclusion[];
    /** Always sent: `{mode: 'none'}` when nothing is selected, never null or absent. */
    last_completed_service: SmriqLastCompletedServiceInput;
}

/** GET .../smriq/context — the only GET, and the only cacheable call in the journey. */
export const fetchSmriqContext = async (productId: string): Promise<SmriqContext> =>
    (
        await api.get<{ data: SmriqContext }>(`${smrPath}/products/${productId}/smriq/context`)
    ).data.data;

/** POST .../smriq/vehicle/confirm */
export const confirmSmriqVehicle = async (
    productId: string,
    payload: ConfirmSmriqVehiclePayload,
): Promise<SmriqVehicleConfirmResult> =>
    (
        await api.post<{ data: SmriqVehicleConfirmResult }>(
            `${smrPath}/products/${productId}/smriq/vehicle/confirm`,
            payload,
        )
    ).data.data;

/** POST .../smriq/schedule-preview */
export const fetchSmriqSchedulePreview = async (
    productId: string,
    sessionId: string,
    currentMileage: number,
    annualMileage: number,
): Promise<SmriqSchedulePreviewResult> =>
    (
        await api.post<{ data: SmriqSchedulePreviewResult }>(
            `${smrPath}/products/${productId}/smriq/schedule-preview`,
            {
                service_plan_session_id: sessionId,
                current_mileage: currentMileage,
                annual_mileage: annualMileage,
            },
        )
    ).data.data;

/** POST .../smriq/generate — rate-budgeted; never fire it on a keystroke or toggle. */
export const generateSmriqSchedule = async (
    productId: string,
    payload: GenerateSmriqSchedulePayload,
): Promise<SmriqGenerateResult> =>
    (
        await api.post<{ data: SmriqGenerateResult }>(
            `${smrPath}/products/${productId}/smriq/generate`,
            payload,
        )
    ).data.data;
